import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Project, 
  TimeEntry, 
  LoginRequest, 
  LoginResponse,
  DashboardStats,
  ProjectCostReport,
  UserHoursReport,
  DailyCostReport,
  WorkRecord,
  WorkRecordCreate,
  WorkRecordUpdate,
  CheckInCreate,
  CheckOutCreate,
  TodayStatus,
  AttendanceStats,
  DashboardWorkStats,
  WorkRecordApproval,
  RecordType,
  RecordStatus,
  SystemStats,
  UserManagementStats,
  ApprovalStats,
  ManagerStats,
  ExpenseCategory,
  ExpenseCategoryCreate,
  ExpenseCategoryUpdate,
  ProjectExpense,
  ProjectExpenseCreate,
  ProjectExpenseUpdate,
  ProjectExpenseApproval,
  ProjectBudget,
  ProjectBudgetCreate,
  ProjectBudgetUpdate,
  ProjectBudgetCategory,
  ProjectBudgetCategoryCreate,
  ProjectBudgetCategoryUpdate,
  ProjectCostSummary,
  ExpenseStatistics,
  ProjectRevenue,
  ProjectRevenueCreate,
  ProjectRevenueUpdate,
  ProjectRevenueListResponse,
  ProjectRevenueStats,
  ProjectFinancialSummary,
  RevenueType
} from '@/types';

class ApiService {
  private api: AxiosInstance;
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    
    // 定期清理过期缓存
    setInterval(() => this.cleanExpiredCache(), 60000); // 每分钟清理一次
  }

  /**
   * 生成缓存键
   */
  private getCacheKey(url: string, params?: any): string {
    const paramStr = params ? JSON.stringify(params) : '';
    return `${url}:${paramStr}`;
  }

  /**
   * 从缓存获取数据
   */
  private getFromCache(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  /**
   * 设置缓存数据
   */
  private setCache(key: string, data: any, ttl: number = 300000): void { // 默认5分钟
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * 清理过期缓存
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    for (const [key, cached] of this.cache.entries()) {
      if (now - cached.timestamp > cached.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * 清除所有缓存
   */
  public clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * 带缓存和去重的GET请求
   */
  private async cachedGet<T>(
    url: string, 
    params?: any, 
    ttl: number = 300000,
    enableCache: boolean = true
  ): Promise<T> {
    const cacheKey = this.getCacheKey(url, params);
    
    // 检查缓存
    if (enableCache) {
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    // 检查是否有正在进行的请求（去重）
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }
    
    // 发起新请求
    const requestPromise = this.api.get<T>(url, { params })
      .then(response => {
        if (enableCache) {
          this.setCache(cacheKey, response.data, ttl);
        }
        return response.data;
      })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });
    
    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    
    const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async register(credentials: { username: string; real_name: string; email: string; password: string }): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post('/auth/register', credentials);
    return response.data;
  }

  // Users
  async getCurrentUser(): Promise<User> {
    // 用户信息缓存时间较短，1分钟
    return this.cachedGet<User>('/users/me', undefined, 60000);
  }

  async getUsers(skip = 0, limit = 100): Promise<User[]> {
    // 用户列表缓存2分钟
    return this.cachedGet<User[]>('/users', { skip, limit }, 120000);
  }

  async createUser(userData: Partial<User> & { password: string }): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post('/users', userData);
    // 清理相关缓存
    this.clearUserCache();
    return response.data;
  }

  async updateUser(userId: number, userData: Partial<User>): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put(`/users/${userId}`, userData);
    // 清理相关缓存
    this.clearUserCache();
    return response.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await this.api.delete(`/users/${userId}`);
    // 清理相关缓存
    this.clearUserCache();
  }

  /**
   * 清理用户相关缓存
   */
  private clearUserCache(): void {
    for (const key of this.cache.keys()) {
      if (key.includes('/users')) {
        this.cache.delete(key);
      }
    }
  }

  async getPendingUsers(): Promise<User[]> {
    // 待审批用户不缓存，需要实时数据
    return this.cachedGet<User[]>('/users/pending', undefined, 0, false);
  }

  async approveUser(userId: number, approveData: { status: string; role?: string; salary?: number }): Promise<User> {
    const response: AxiosResponse<User> = await this.api.post(`/users/${userId}/approve`, approveData);
    return response.data;
  }

  async resetUserPassword(userId: number, resetData: { new_password: string }): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post(`/users/${userId}/reset-password`, resetData);
    return response.data;
  }

  async updateUserSalary(userId: number, salaryData: { salary_type: string; amount: number }): Promise<User> {
    const response: AxiosResponse<User> = await this.api.put(`/users/${userId}/salary`, salaryData);
    return response.data;
  }

  // Projects
  async getProjects(skip = 0, limit = 100): Promise<Project[]> {
    // 项目列表缓存5分钟
    return this.cachedGet<Project[]>('/projects', { skip, limit }, 300000);
  }

  async getProject(projectId: number): Promise<Project> {
    // 单个项目信息缓存3分钟
    return this.cachedGet<Project>(`/projects/${projectId}`, undefined, 180000);
  }

  async createProject(projectData: Partial<Project>): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.post('/projects', projectData);
    // 清理相关缓存
    this.clearProjectCache();
    return response.data;
  }

  async updateProject(projectId: number, projectData: Partial<Project>): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.put(`/projects/${projectId}`, projectData);
    // 清理相关缓存
    this.clearProjectCache();
    return response.data;
  }

  async deleteProject(projectId: number): Promise<void> {
    await this.api.delete(`/projects/${projectId}`);
    // 清理相关缓存
    this.clearProjectCache();
  }

  /**
   * 清理项目相关缓存
   */
  private clearProjectCache(): void {
    for (const key of this.cache.keys()) {
      if (key.includes('/projects')) {
        this.cache.delete(key);
      }
    }
  }

  async addProjectMember(projectId: number, userId: number, role = 'member'): Promise<void> {
    await this.api.post(`/projects/${projectId}/members`, { project_id: projectId, user_id: userId, role });
  }

  async removeProjectMember(projectId: number, userId: number): Promise<void> {
    await this.api.delete(`/projects/${projectId}/members/${userId}`);
  }

  async updateProjectManager(projectId: number, managerId: number | null): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.put(`/projects/${projectId}/manager`, { manager_id: managerId });
    return response.data;
  }

  async updateProjectStatus(projectId: number, status: string): Promise<Project> {
    const response: AxiosResponse<Project> = await this.api.put(`/projects/${projectId}/status`, { status });
    return response.data;
  }

  async getRecentProjects(limit = 10): Promise<Project[]> {
    // 最近项目缓存1分钟
    return this.cachedGet<Project[]>('/projects/recent', { limit }, 60000);
  }

  async getCostAccessibleProjects(skip = 0, limit = 100): Promise<Project[]> {
    // 成本可见项目缓存3分钟
    return this.cachedGet<Project[]>('/projects/cost-accessible', { skip, limit }, 180000);
  }

  // Time Entries
  async getTimeEntries(params?: {
    skip?: number;
    limit?: number;
    project_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<TimeEntry[]> {
    const response: AxiosResponse<TimeEntry[]> = await this.api.get('/time-entries', { params });
    return response.data;
  }

  async getTimeEntry(entryId: number): Promise<TimeEntry> {
    const response: AxiosResponse<TimeEntry> = await this.api.get(`/time-entries/${entryId}`);
    return response.data;
  }

  async createTimeEntry(entryData: Partial<TimeEntry>): Promise<TimeEntry> {
    const response: AxiosResponse<TimeEntry> = await this.api.post('/time-entries', entryData);
    return response.data;
  }

  async updateTimeEntry(entryId: number, entryData: Partial<TimeEntry>): Promise<TimeEntry> {
    const response: AxiosResponse<TimeEntry> = await this.api.put(`/time-entries/${entryId}`, entryData);
    return response.data;
  }

  async deleteTimeEntry(entryId: number): Promise<void> {
    await this.api.delete(`/time-entries/${entryId}`);
  }

  // Reports
  async getDashboardStats(): Promise<DashboardStats> {
    const response: AxiosResponse<DashboardStats> = await this.api.get('/reports/dashboard');
    return response.data;
  }

  async getProjectCostReport(params?: {
    start_date?: string;
    end_date?: string;
    project_id?: number;
  }): Promise<ProjectCostReport[]> {
    const response: AxiosResponse<ProjectCostReport[]> = await this.api.get('/reports/project-costs', { params });
    return response.data;
  }

  async getUserHoursReport(params?: {
    start_date?: string;
    end_date?: string;
    user_id?: number;
  }): Promise<UserHoursReport[]> {
    const response: AxiosResponse<UserHoursReport[]> = await this.api.get('/reports/user-hours', { params });
    return response.data;
  }

  async getDailyCostReport(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<DailyCostReport[]> {
    const response: AxiosResponse<DailyCostReport[]> = await this.api.get('/reports/daily-costs', { params });
    return response.data;
  }

  // Work Records
  async checkIn(checkInData?: CheckInCreate): Promise<WorkRecord> {
    const response: AxiosResponse<WorkRecord> = await this.api.post('/work-records/check-in', checkInData || {});
    return response.data;
  }

  async checkOut(checkOutData: CheckOutCreate): Promise<WorkRecord> {
    const response: AxiosResponse<WorkRecord> = await this.api.post('/work-records/check-out', checkOutData);
    return response.data;
  }

  async getTodayStatus(): Promise<TodayStatus> {
    const response: AxiosResponse<TodayStatus> = await this.api.get('/work-records/today-status');
    return response.data;
  }

  async getWorkRecords(params?: {
    skip?: number;
    limit?: number;
    record_type?: RecordType;
    start_date?: string;
    end_date?: string;
    status?: RecordStatus;
  }): Promise<WorkRecord[]> {
    const response: AxiosResponse<WorkRecord[]> = await this.api.get('/work-records/', { params });
    return response.data;
  }

  async getWorkRecord(recordId: number): Promise<WorkRecord> {
    const response: AxiosResponse<WorkRecord> = await this.api.get(`/work-records/${recordId}`);
    return response.data;
  }

  async createWorkRecord(recordData: WorkRecordCreate): Promise<WorkRecord> {
    const response: AxiosResponse<WorkRecord> = await this.api.post('/work-records/', recordData);
    return response.data;
  }

  async updateWorkRecord(recordId: number, recordData: WorkRecordUpdate): Promise<WorkRecord> {
    const response: AxiosResponse<WorkRecord> = await this.api.put(`/work-records/${recordId}`, recordData);
    return response.data;
  }

  async deleteWorkRecord(recordId: number): Promise<void> {
    await this.api.delete(`/work-records/${recordId}`);
  }

  async adminDeleteWorkRecord(recordId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/work-records/admin/${recordId}`);
    return response.data;
  }

  async getPendingApprovals(skip = 0, limit = 100): Promise<WorkRecord[]> {
    const response: AxiosResponse<WorkRecord[]> = await this.api.get('/work-records/pending/list', {
      params: { skip, limit },
    });
    return response.data;
  }

  async approveRecords(approvalData: WorkRecordApproval): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post('/work-records/approve', approvalData);
    return response.data;
  }

  // 新增：团队工作记录查看（项目管理者）
  async getTeamWorkRecords(params?: {
    skip?: number;
    limit?: number;
    record_type?: RecordType;
    start_date?: string;
    end_date?: string;
    status?: RecordStatus;
    project_id?: number;
    user_id?: number;
  }): Promise<WorkRecord[]> {
    const response: AxiosResponse<WorkRecord[]> = await this.api.get('/work-records/team', { params });
    return response.data;
  }

  // 新增：获取团队工作记录的日期列表（项目管理者）
  async getTeamWorkDates(): Promise<string[]> {
    const response: AxiosResponse<string[]> = await this.api.get('/work-records/team/dates');
    return response.data;
  }

  // 新增：全局工作记录查看（督导）
  async getAllWorkRecords(params?: {
    skip?: number;
    limit?: number;
    record_type?: RecordType;
    start_date?: string;
    end_date?: string;
    status?: RecordStatus;
    project_id?: number;
    user_id?: number;
  }): Promise<WorkRecord[]> {
    const response: AxiosResponse<WorkRecord[]> = await this.api.get('/work-records/all', { params });
    return response.data;
  }

  async getAttendanceStats(startDate: string, endDate: string): Promise<AttendanceStats[]> {
    const response: AxiosResponse<AttendanceStats[]> = await this.api.get('/work-records/attendance/stats', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  }

  async getDashboardWorkStats(): Promise<DashboardWorkStats> {
    const response: AxiosResponse<DashboardWorkStats> = await this.api.get('/work-records/dashboard/stats');
    return response.data;
  }

  // 管理员统计API
  async getSystemStats(): Promise<SystemStats> {
    const response: AxiosResponse<SystemStats> = await this.api.get('/reports/admin/system-stats');
    return response.data;
  }

  async getUserManagementStats(): Promise<UserManagementStats> {
    const response: AxiosResponse<UserManagementStats> = await this.api.get('/reports/admin/user-management-stats');
    return response.data;
  }

  async getApprovalStats(): Promise<ApprovalStats> {
    const response: AxiosResponse<ApprovalStats> = await this.api.get('/reports/admin/approval-stats');
    return response.data;
  }

  // 项目管理者统计API
  async getManagerStats(): Promise<ManagerStats> {
    const response: AxiosResponse<ManagerStats> = await this.api.get('/reports/manager/stats');
    return response.data;
  }

  // 支出类型管理API
  async getExpenseCategories(params?: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
  }): Promise<ExpenseCategory[]> {
    const response: AxiosResponse<ExpenseCategory[]> = await this.api.get('/expenses/categories', { params });
    return response.data;
  }

  async createExpenseCategory(categoryData: ExpenseCategoryCreate): Promise<ExpenseCategory> {
    const response: AxiosResponse<ExpenseCategory> = await this.api.post('/expenses/categories', categoryData);
    return response.data;
  }

  async updateExpenseCategory(categoryId: number, categoryData: ExpenseCategoryUpdate): Promise<ExpenseCategory> {
    const response: AxiosResponse<ExpenseCategory> = await this.api.put(`/expenses/categories/${categoryId}`, categoryData);
    return response.data;
  }

  // 项目支出管理API
  async getProjectExpenses(projectId: number, params?: {
    skip?: number;
    limit?: number;
    status?: string;
    category_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<ProjectExpense[]> {
    const response: AxiosResponse<ProjectExpense[]> = await this.api.get(`/expenses/projects/${projectId}/expenses`, { params });
    return response.data;
  }

  async createProjectExpense(expenseData: ProjectExpenseCreate): Promise<ProjectExpense> {
    const response: AxiosResponse<ProjectExpense> = await this.api.post(`/expenses/projects/${expenseData.project_id}/expenses`, expenseData);
    return response.data;
  }

  async updateProjectExpense(expenseId: number, expenseData: ProjectExpenseUpdate): Promise<ProjectExpense> {
    const response: AxiosResponse<ProjectExpense> = await this.api.put(`/expenses/expenses/${expenseId}`, expenseData);
    return response.data;
  }

  async deleteProjectExpense(expenseId: number): Promise<{ detail: string }> {
    const response: AxiosResponse<{ detail: string }> = await this.api.delete(`/expenses/expenses/${expenseId}`);
    return response.data;
  }

  async approveExpense(expenseId: number, approvalData: ProjectExpenseApproval): Promise<ProjectExpense> {
    const response: AxiosResponse<ProjectExpense> = await this.api.put(`/expenses/expenses/${expenseId}/approve`, approvalData);
    return response.data;
  }

  // 项目预算管理API
  async getProjectBudget(projectId: number): Promise<ProjectBudget> {
    const response: AxiosResponse<ProjectBudget> = await this.api.get(`/expenses/projects/${projectId}/budget`);
    return response.data;
  }

  async createProjectBudget(projectId: number, budgetData: ProjectBudgetCreate): Promise<ProjectBudget> {
    const response: AxiosResponse<ProjectBudget> = await this.api.post(`/expenses/projects/${projectId}/budget`, budgetData);
    return response.data;
  }

  async updateProjectBudget(projectId: number, budgetData: ProjectBudgetUpdate): Promise<ProjectBudget> {
    const response: AxiosResponse<ProjectBudget> = await this.api.put(`/expenses/projects/${projectId}/budget`, budgetData);
    return response.data;
  }

  // 成本统计API
  async getProjectCostSummary(projectId: number): Promise<ProjectCostSummary> {
    const response: AxiosResponse<ProjectCostSummary> = await this.api.get(`/expenses/projects/${projectId}/cost-summary`);
    return response.data;
  }

  async getExpenseStatistics(projectId: number): Promise<ExpenseStatistics> {
    const response: AxiosResponse<ExpenseStatistics> = await this.api.get(`/expenses/projects/${projectId}/expense-statistics`);
    return response.data;
  }

  // 内部成本管理API
  async calculateInternalCosts(data: {
    target_date: string;
    user_ids?: number[];
    force_recalculate?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/internal-costs/calculate', data);
    return response.data;
  }

  async calculateInternalCostsRange(params: {
    start_date: string;
    end_date: string;
    user_ids?: number[];
    force_recalculate?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/internal-costs/calculate-range', null, { params });
    return response.data;
  }

  async getProjectInternalCosts(projectId: number, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<any> {
    const response = await this.api.get(`/internal-costs/projects/${projectId}`, { params });
    return response.data;
  }

  async getInternalCostCalculationHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.api.get('/internal-costs/calculation-history', { params });
    return response.data;
  }

  async generateInternalExpenseRecords(data: {
    target_date: string;
    force_recreate?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/internal-costs/generate-expenses', data);
    return response.data;
  }

  async getInternalCostsSummary(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<any> {
    const response = await this.api.get('/internal-costs/summary', { params });
    return response.data;
  }

  // 定时任务调度器 API
  async getSchedulerStatus(): Promise<any> {
    const response = await this.api.get('/scheduler/status');
    return response.data;
  }

  async manualCalculateCosts(data: {
    target_date: string;
    force_recalculate?: boolean;
  }): Promise<any> {
    const response = await this.api.post('/scheduler/manual-calculate', data);
    return response.data;
  }

  async calculateYesterdayCosts(force_recalculate: boolean = false): Promise<any> {
    const response = await this.api.post('/scheduler/calculate-yesterday', null, {
      params: { force_recalculate }
    });
    return response.data;
  }

  async startScheduler(): Promise<any> {
    const response = await this.api.post('/scheduler/start');
    return response.data;
  }

  async stopScheduler(): Promise<any> {
    const response = await this.api.post('/scheduler/stop');
    return response.data;
  }

  async restartScheduler(): Promise<any> {
    const response = await this.api.post('/scheduler/restart');
    return response.data;
  }

  // 项目收入管理 API
  async getProjectRevenues(params?: {
    skip?: number;
    limit?: number;
    project_id?: number;
    revenue_type?: RevenueType;
    start_date?: string;
    end_date?: string;
  }): Promise<ProjectRevenueListResponse> {
    const response: AxiosResponse<ProjectRevenueListResponse> = await this.api.get('/project-revenues', { params });
    return response.data;
  }

  async getProjectRevenue(revenueId: number): Promise<ProjectRevenue> {
    const response: AxiosResponse<ProjectRevenue> = await this.api.get(`/project-revenues/${revenueId}`);
    return response.data;
  }

  async createProjectRevenue(revenueData: ProjectRevenueCreate): Promise<ProjectRevenue> {
    const response: AxiosResponse<ProjectRevenue> = await this.api.post('/project-revenues', revenueData);
    return response.data;
  }

  async updateProjectRevenue(revenueId: number, revenueData: ProjectRevenueUpdate): Promise<ProjectRevenue> {
    const response: AxiosResponse<ProjectRevenue> = await this.api.put(`/project-revenues/${revenueId}`, revenueData);
    return response.data;
  }

  async deleteProjectRevenue(revenueId: number): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.delete(`/project-revenues/${revenueId}`);
    return response.data;
  }

  async confirmProjectRevenue(revenueId: number): Promise<ProjectRevenue> {
    const response: AxiosResponse<ProjectRevenue> = await this.api.post(`/project-revenues/${revenueId}/confirm`);
    return response.data;
  }

  async cancelProjectRevenue(revenueId: number): Promise<ProjectRevenue> {
    const response: AxiosResponse<ProjectRevenue> = await this.api.post(`/project-revenues/${revenueId}/cancel`);
    return response.data;
  }

  async getRevenueStats(params?: {
    project_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<ProjectRevenueStats> {
    const response: AxiosResponse<ProjectRevenueStats> = await this.api.get('/project-revenues/stats/summary', { params });
    return response.data;
  }

  async getProjectFinancialSummary(projectId: number): Promise<ProjectFinancialSummary> {
    const response: AxiosResponse<ProjectFinancialSummary> = await this.api.get(`/projects/${projectId}/financial-summary`);
    return response.data;
  }

  async getProjectRevenueStats(projectId: number, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ProjectRevenueStats> {
    const response: AxiosResponse<ProjectRevenueStats> = await this.api.get(`/projects/${projectId}/revenue-stats`, { params });
    return response.data;
  }
}

export default new ApiService();